import React, { useEffect, useMemo, useState } from "react";
import { Lock, Phone, Check, ShieldCheck, Calendar, ExternalLink } from "lucide-react";
import Navigation from "../components/site/Navigation";
import Footer from "../components/site/Footer";
import Reveal from "../components/site/Reveal";
import LivingSectionTitle from "../components/site/LivingSectionTitle";
import VisualAnchor from "../components/site/ui/VisualAnchor";
import OrientationResetCheckoutHost from "../components/site/OrientationResetCheckoutHost";
import { api } from "../lib/api";
import { trackEvent } from "../lib/track";
import { pollCheckoutStatus } from "../lib/checkout";

/**
 * Clarity Reset Session — Schedule.
 *
 * Two states:
 *  - LOCKED (no session_id, unverified, or payment_status != paid).
 *    Renders the brand artifact, the $149 invitation, and a "Pay to
 *    Book" CTA that opens the Stripe Checkout flow via ReservationModal.
 *  - UNLOCKED (verified paid session_id for product == "orientation_reset").
 *    Renders the Calendly inline scheduler + Preparation Checklist.
 *
 * Verification is server-side: the page calls
 * `GET /api/orientation-reset/verify/{session_id}` which returns the
 * minimal paid+product confirmation. Stripe metadata is the source of
 * truth — the URL alone cannot unlock the schedule UI.
 *
 * When the visitor lands here directly from the Stripe redirect, the
 * payment_transactions row may still be `pending` (the
 * /checkout-status polling endpoint is what flips it to `paid`). We
 * call pollCheckoutStatus once on arrival so the verify endpoint
 * returns `paid: true` within a few seconds without the visitor
 * needing to refresh.
 */

// The Preparation Checklist — the six prompts Cyrus wants every paid
// attendee to walk in with. Surfaced on the unlocked state directly
// under the Calendly embed.
const PREP_QUESTIONS = [
  "What question have I been circling but not naming?",
  "Where do I feel I am forcing instead of flowing?",
  "What decision, idea, offer, or direction feels most alive right now?",
  "What have I already tried that has not fully worked?",
  "What would make this session valuable?",
  "What am I ready to see more clearly?",
];

const getSessionId = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get("session_id");
};

// Build the Calendly link with prefilled name/email so the scheduler
// doesn't ask the customer for data we already have from Stripe.
const buildCalendlyHref = (baseUrl, name, email) => {
  if (!baseUrl) return null;
  try {
    const u = new URL(baseUrl);
    if (name) u.searchParams.set("name", name);
    if (email) u.searchParams.set("email", email);
    // Hide Calendly's gdpr footer for the embedded view; harmless on link view.
    u.searchParams.set("hide_gdpr_banner", "1");
    return u.toString();
  } catch {
    return baseUrl;
  }
};

const SchedulePage = () => {
  const sessionId = getSessionId();
  const [verifyState, setVerifyState] = useState({
    status: sessionId ? "checking" : "unpaid",
    paid: false,
    reason: null,
    data: null,
  });
  const [pollAttempt, setPollAttempt] = useState(0);

  useEffect(() => {
    document.title = "Clarity Reset Session · Schedule · Authentic Momentum";
  }, []);

  // Verify the session on mount and poll Stripe status if pending.
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;

    const verify = async () => {
      try {
        const { data } = await api.get(
          `/orientation-reset/verify/${encodeURIComponent(sessionId)}`
        );
        if (cancelled) return null;
        return data;
      } catch {
        return { paid: false, reason: "error" };
      }
    };

    const run = async () => {
      let result = await verify();
      if (cancelled) return;

      if (result?.paid) {
        setVerifyState({ status: "paid", paid: true, reason: null, data: result });
        trackEvent("orientation_reset_schedule_unlocked", { session_id: sessionId });
        api
          .post("/orientation-reset/schedule-viewed", { session_id: sessionId })
          .catch(() => {});
        return;
      }

      // If pending, kick the status polling endpoint (this is what
      // actually flips payment_transactions from initiated → paid
      // after the Stripe redirect). Then re-verify.
      if (result?.reason === "pending") {
        try {
          await pollCheckoutStatus(sessionId, { maxAttempts: 6, intervalMs: 2000 });
        } catch {
          // Ignored — the next verify attempt will surface the state.
        }
        if (cancelled) return;
        result = await verify();
        if (cancelled) return;
        if (result?.paid) {
          setVerifyState({ status: "paid", paid: true, reason: null, data: result });
          trackEvent("orientation_reset_schedule_unlocked", { session_id: sessionId });
          api
            .post("/orientation-reset/schedule-viewed", { session_id: sessionId })
            .catch(() => {});
          return;
        }
      }

      setVerifyState({
        status: "unpaid",
        paid: false,
        reason: result?.reason || "unknown",
        data: null,
      });
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [sessionId, pollAttempt]);

  const isChecking = verifyState.status === "checking";
  const isUnlocked = verifyState.paid;
  const customerName = verifyState.data?.customer_name;
  const customerEmail = verifyState.data?.customer_email;
  const calendlyHref = useMemo(
    () => buildCalendlyHref(verifyState.data?.calendly_url, customerName, customerEmail),
    [verifyState.data?.calendly_url, customerName, customerEmail]
  );

  // The Calendly inline embed has its own JS widget. We lazy-load the
  // script ONLY when the unlocked state mounts, so locked visitors
  // never pay the bundle cost. The widget itself reads the
  // `data-url` attribute on the target div and mounts inside.
  useEffect(() => {
    if (!isUnlocked || !calendlyHref) return undefined;
    const existing = document.querySelector('script[data-cal-embed-script="1"]');
    if (existing) return undefined;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://assets.calendly.com/assets/external/widget.css";
    link.dataset.calEmbedStylesheet = "1";
    document.head.appendChild(link);
    const s = document.createElement("script");
    s.src = "https://assets.calendly.com/assets/external/widget.js";
    s.async = true;
    s.dataset.calEmbedScript = "1";
    document.body.appendChild(s);
    return undefined;
  }, [isUnlocked, calendlyHref]);

  return (
    <>
      <Navigation />
      <main
        data-testid="orientation-reset-schedule"
        className="min-h-screen bg-themed noise-overlay pt-28 pb-24"
      >
        {/* HERO */}
        <section className="relative max-w-4xl mx-auto px-6 md:px-10 text-center">
          <Reveal>
            <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-6">
              The Clarity Reset Session · $149
            </p>
            <LivingSectionTitle
              parts={[
                { text: "Book the" },
                { text: "Clarity Reset Session.", italic: true },
              ]}
              align="center"
              variant="signal"
              className="text-4xl sm:text-5xl lg:text-6xl text-themed"
            />
            <p className="mt-8 text-themed-muted leading-relaxed max-w-2xl mx-auto">
              A focused session to identify the hidden frame, signal, or
              constraint shaping your current momentum, and clarify the next
              aligned move.
            </p>
          </Reveal>

          <div className="mt-14 mx-auto max-w-3xl" data-testid="schedule-threshold-anchor">
            <VisualAnchor
              src="/assets/brand/threshold.webp"
              alt="The threshold of the Clarity Reset Session."
              aspect="aspect-[1402/1122] sm:aspect-[1402/1000]"
              className="rounded-md border border-gold/15 bg-black/30"
              imgClassName="object-contain"
            />
            <p className="mt-4 text-[10px] tracking-[0.4em] uppercase text-gold/80">
              The Threshold
            </p>
          </div>
        </section>

        {/* STATE-DRIVEN BLOCK */}
        <section className="relative max-w-3xl mx-auto px-6 md:px-10 mt-24">
          <Reveal>
            {isChecking && (
              <div
                data-testid="schedule-checking-state"
                className="glass-card p-10 text-center border-gold/30"
              >
                <p className="text-[10px] tracking-[0.4em] uppercase text-gold/80 mb-3">
                  Verifying payment…
                </p>
                <p className="text-themed-muted leading-relaxed">
                  Hold for a moment while we confirm your Stripe receipt. This
                  usually takes 3–8 seconds.
                </p>
              </div>
            )}

            {!isChecking && isUnlocked && (
              <div
                data-testid="schedule-unlocked-state"
                className="glass-card p-8 sm:p-10 border-gold/40"
              >
                <div className="w-12 h-12 mx-auto border border-gold/50 flex items-center justify-center mb-6 bg-gold/5">
                  <ShieldCheck className="text-gold" size={20} strokeWidth={1.2} />
                </div>
                <p className="text-[10px] tracking-[0.4em] uppercase text-gold mb-3 text-center">
                  Payment Confirmed
                </p>
                <h2 className="font-serif text-2xl sm:text-3xl text-themed text-center mb-3 leading-tight">
                  {customerName
                    ? `${customerName}, choose your time.`
                    : "Choose your time."}
                </h2>
                <p className="text-themed-muted leading-relaxed text-center max-w-xl mx-auto">
                  Use the scheduler below to pick a 30-minute window that works
                  for you. A confirmation will land in your inbox immediately.
                </p>

                {/* Calendly embed */}
                {calendlyHref ? (
                  <>
                    <div
                      data-testid="schedule-calendly-embed"
                      className="calendly-inline-widget mt-10 mx-auto"
                      data-url={calendlyHref}
                      style={{ minWidth: "320px", height: "720px" }}
                    />
                    <div className="mt-6 text-center">
                      <a
                        href={calendlyHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="schedule-calendly-open"
                        className="text-[11px] tracking-[0.3em] uppercase text-gold/85 hover:text-gold border-b border-gold/35 hover:border-gold transition-colors inline-flex items-center gap-2"
                      >
                        Open in new tab <ExternalLink size={11} strokeWidth={1.5} />
                      </a>
                    </div>
                  </>
                ) : (
                  <div className="mt-10 text-center text-themed-muted text-sm">
                    Booking link is being finalized. Email{" "}
                    <a
                      href="mailto:cyrus@authentic-momentum.com"
                      className="text-gold hover:underline"
                    >
                      cyrus@authentic-momentum.com
                    </a>{" "}
                    to schedule directly.
                  </div>
                )}

                <p className="mt-8 text-[10px] tracking-[0.32em] uppercase text-themed-muted text-center">
                  Session ID · {sessionId?.slice(-12)}
                </p>
              </div>
            )}

            {!isChecking && !isUnlocked && (
              <div
                data-testid="schedule-locked-state"
                className="glass-card p-10 text-center border-gold/30"
              >
                <div className="w-12 h-12 mx-auto border border-gold/40 flex items-center justify-center mb-6">
                  <Lock className="text-gold" size={18} strokeWidth={1.2} />
                </div>
                <p className="text-[10px] tracking-[0.4em] uppercase text-gold/80 mb-3">
                  Booking Locked · $149
                </p>
                <h2 className="font-serif text-2xl sm:text-3xl text-themed mb-4 leading-tight">
                  Reserve your Clarity Reset Session to unlock the scheduler.
                </h2>
                <p className="text-themed-muted leading-relaxed max-w-xl mx-auto">
                  Payment is the gate, not the goal. The fee preserves the
                  seriousness of the conversation and the calendar that holds
                  it. Most sessions are booked within 48 hours of payment.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
                  <button
                    type="button"
                    data-testid="schedule-pay-cta"
                    onClick={() => {
                      trackEvent("orientation_reset_pay_cta_clicked", {
                        surface: "schedule_locked_state",
                      });
                      window.dispatchEvent(
                        new CustomEvent("am:open-orientation-reset-checkout")
                      );
                    }}
                    className="btn-gold inline-flex items-center gap-2 justify-center"
                  >
                    <Calendar size={14} strokeWidth={1.5} />
                    Book the Clarity Reset Session · $149
                  </button>
                  <a
                    href="tel:+19177228598"
                    data-testid="schedule-locked-call-cta"
                    className="btn-ghost-gold inline-flex items-center gap-2 justify-center"
                  >
                    <Phone size={13} strokeWidth={1.5} />
                    Talk First · (917) 722-8598
                  </a>
                </div>
                {verifyState.reason === "wrong_product" && (
                  <p className="mt-6 text-[12px] text-themed-muted">
                    That session_id is for a different product. Please use the
                    link from your Clarity Reset Session receipt.
                  </p>
                )}
                {verifyState.reason === "pending" && sessionId && (
                  <button
                    type="button"
                    onClick={() => setPollAttempt((n) => n + 1)}
                    data-testid="schedule-retry-verify"
                    className="mt-6 text-[11px] tracking-[0.3em] uppercase text-gold/80 hover:text-gold border-b border-gold/40 hover:border-gold transition-colors"
                  >
                    Refresh payment status →
                  </button>
                )}
                {/* Editorial bridge — quietly directs visitors who are
                    not ready to commit yet toward the journal piece
                    that names the exact friction the Clarity Reset Session
                    resolves. Low visual weight; sits below the primary
                    CTAs so it never competes with them. */}
                <p
                  data-testid="schedule-journal-bridge"
                  className="mt-8 text-[12px] text-themed-muted leading-relaxed"
                >
                  Not ready to book yet?{" "}
                  <a
                    href="/journal/sabotaging-the-wrong-plan"
                    onClick={() =>
                      trackEvent("schedule_journal_bridge_clicked", {
                        slug: "sabotaging-the-wrong-plan",
                      })
                    }
                    className="text-gold/90 hover:text-gold border-b border-gold/35 hover:border-gold transition-colors"
                  >
                    Read &ldquo;Sabotaging the Wrong Plan&rdquo; →
                  </a>
                </p>
              </div>
            )}
          </Reveal>
        </section>

        {/* PREPARATION CHECKLIST — visible in all states so anyone
            researching the session can see what arrival looks like. */}
        <section
          className="relative max-w-3xl mx-auto px-6 md:px-10 mt-24"
          data-testid="schedule-prep-checklist"
        >
          <Reveal>
            <p className="text-[10px] tracking-[0.4em] uppercase text-gold/85 text-center mb-3">
              Preparation Checklist
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl text-themed text-center leading-tight mb-10">
              Six questions to <span className="italic gold-gradient-text">sit with first.</span>
            </h2>
            <p className="text-themed-muted text-center max-w-xl mx-auto leading-relaxed mb-10">
              You do not need polished answers, only honest ones. Bring the
              friction, not the performance.
            </p>
            <ol className="space-y-4 max-w-2xl mx-auto">
              {PREP_QUESTIONS.map((q, i) => (
                <li
                  key={q}
                  data-testid={`schedule-prep-${i + 1}`}
                  className="flex items-start gap-4 border border-gold/15 hover:border-gold/35 transition-colors p-5 bg-black/30"
                >
                  <span className="text-gold text-[10px] tracking-[0.3em] uppercase pt-1 min-w-[28px]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-themed text-[15px] leading-relaxed">{q}</span>
                </li>
              ))}
            </ol>
          </Reveal>
        </section>

        {/* HOW TO ARRIVE — kept from the previous design as a tone-setter */}
        <section className="relative max-w-4xl mx-auto px-6 md:px-10 mt-24">
          <Reveal>
            <p className="text-[10px] tracking-[0.4em] uppercase text-gold/80 text-center mb-3">
              How to arrive
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl text-themed text-center leading-tight mb-10">
              Honesty over <span className="italic gold-gradient-text">performance.</span>
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-6" data-testid="schedule-notebook-anchor">
                <VisualAnchor
                  src="/assets/brand/notebook.webp"
                  alt="A notebook of attention. The Living Practice."
                  aspect="aspect-[1536/1024]"
                  className="rounded-md border border-gold/15 bg-black/30"
                  imgClassName="object-cover object-center"
                />
                <p className="mt-3 text-center text-[10px] tracking-[0.4em] uppercase text-gold/80">
                  The Living Practice
                </p>
              </div>

              <ul className="lg:col-span-6 space-y-4">
                {[
                  "Bring honesty over performance. The session is a mirror, not a stage.",
                  "Name the question you keep circling.",
                  "Note one constraint you suspect is shaping the path.",
                  "Allow the conversation to surface what you already know.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-themed text-[15px] leading-relaxed">
                    <Check className="text-gold mt-0.5 shrink-0" size={15} strokeWidth={1.5} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </section>
      </main>
      <OrientationResetCheckoutHost />
      <Footer />
    </>
  );
};

export default SchedulePage;
